import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface GscConnectionInfo {
  id: string
  email: string
  displayName: string | null
  hasGsc: boolean
  hasAnalytics: boolean
  createdAt: string
}

export interface GscSiteMatch {
  email: string
  siteUrl: string
  permissionLevel: string
}

export interface GscDailyRow {
  date: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscQueryRow {
  query: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscTotals {
  clicks: number
  impressions: number
  ctr: number
  position?: number
}

export interface GscIndexStatus {
  verdict?: string
  coverageState?: string
  robotsTxtState?: string
  indexingState?: string
  lastCrawlTime?: string
  pageFetchState?: string
  googleCanonical?: string
  userCanonical?: string
  crawledAs?: string
}

/** List all connected GSC accounts */
export function useGscConnections() {
  return useQuery({
    queryKey: ['gsc', 'connections'],
    queryFn: () => apiClient.get<{ connections: GscConnectionInfo[]; canManage: boolean }>('/api/gsc/connections'),
  })
}

/** List all GSC sites across all connected accounts */
export function useGscSites() {
  return useQuery({
    queryKey: ['gsc', 'sites'],
    queryFn: () => apiClient.get<{ sites: Array<{ siteUrl: string; permissionLevel: string; connectionId: string; email: string }> }>('/api/gsc/sites'),
    staleTime: 5 * 60 * 1000,
  })
}

/** Disconnect a GSC account */
export function useDisconnectGsc() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/gsc/connections/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc'] })
    },
  })
}

/** Check GSC connection status for a site's domain */
export function useGscStatus(siteId: string) {
  return useQuery({
    queryKey: ['gsc', 'status', siteId],
    queryFn: () => apiClient.get<{ connected: boolean; accounts: GscSiteMatch[] }>(
      `/api/sites/${siteId}/gsc/status`
    ),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Site-level daily performance trends */
export function useGscPerformance(siteId: string, siteUrl: string, days = 28) {
  return useQuery({
    queryKey: ['gsc', 'performance', siteId, siteUrl, days],
    queryFn: () => apiClient.get<{
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      totals: GscTotals
      daily: GscDailyRow[]
    }>(`/api/sites/${siteId}/gsc/performance`, {
      params: { siteUrl, days },
    }),
    enabled: !!siteId && !!siteUrl,
    staleTime: 5 * 60 * 1000,
  })
}

/** Top search queries for the domain */
export function useGscQueries(siteId: string, siteUrl: string, days = 28, limit = 25) {
  return useQuery({
    queryKey: ['gsc', 'queries', siteId, siteUrl, days, limit],
    queryFn: () => apiClient.get<{
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      totals: GscTotals
      queries: GscQueryRow[]
    }>(`/api/sites/${siteId}/gsc/queries`, {
      params: { siteUrl, days, limit },
    }),
    enabled: !!siteId && !!siteUrl,
    staleTime: 5 * 60 * 1000,
  })
}

export interface GscLandingPageRow {
  page: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscCountryRow {
  country: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscDeviceRow {
  device: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

/** Top landing pages by search performance */
export function useGscLandingPages(siteId: string, siteUrl: string, days = 28) {
  return useQuery({
    queryKey: ['gsc', 'landing-pages', siteId, siteUrl, days],
    queryFn: () => apiClient.get<{
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      pages: GscLandingPageRow[]
    }>(`/api/sites/${siteId}/gsc/landing-pages`, {
      params: { siteUrl, days },
    }),
    enabled: !!siteId && !!siteUrl,
    staleTime: 5 * 60 * 1000,
  })
}

/** Performance breakdown by country */
export function useGscCountries(siteId: string, siteUrl: string, days = 28) {
  return useQuery({
    queryKey: ['gsc', 'countries', siteId, siteUrl, days],
    queryFn: () => apiClient.get<{
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      countries: GscCountryRow[]
    }>(`/api/sites/${siteId}/gsc/countries`, {
      params: { siteUrl, days },
    }),
    enabled: !!siteId && !!siteUrl,
    staleTime: 5 * 60 * 1000,
  })
}

/** Performance breakdown by device type */
export function useGscDevices(siteId: string, siteUrl: string, days = 28) {
  return useQuery({
    queryKey: ['gsc', 'devices', siteId, siteUrl, days],
    queryFn: () => apiClient.get<{
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      devices: GscDeviceRow[]
    }>(`/api/sites/${siteId}/gsc/devices`, {
      params: { siteUrl, days },
    }),
    enabled: !!siteId && !!siteUrl,
    staleTime: 5 * 60 * 1000,
  })
}

/** Per-page GSC metrics + top queries (manual trigger) */
export function useGscPage(siteId: string, siteUrl: string, url: string) {
  return useQuery({
    queryKey: ['gsc', 'page', siteId, siteUrl, url],
    queryFn: () => apiClient.get<{
      url: string
      siteUrl: string
      dateRange: { startDate: string; endDate: string }
      totals: GscTotals & { position: number }
      queries: GscQueryRow[]
    }>(`/api/sites/${siteId}/gsc/page`, {
      params: { siteUrl, url },
    }),
    enabled: false, // Manual trigger via refetch()
    staleTime: 5 * 60 * 1000,
  })
}

/** URL Inspection API result (manual trigger) */
export function useGscInspection(siteId: string, siteUrl: string, url: string) {
  return useQuery({
    queryKey: ['gsc', 'inspect', siteId, siteUrl, url],
    queryFn: () => apiClient.get<{
      url: string
      siteUrl: string
      indexStatus: GscIndexStatus | null
      mobileUsability: { verdict?: string; issues?: Array<{ issueType?: string; message?: string }> } | null
      richResults: { verdict?: string; detectedItems?: Array<{ richResultType?: string }> } | null
    }>(`/api/sites/${siteId}/gsc/inspect`, {
      params: { siteUrl, url },
    }),
    enabled: false, // Manual trigger via refetch()
    staleTime: 5 * 60 * 1000,
  })
}

/** Invalidate server-side GSC cache + client queries */
export function useRefreshGscCache(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete<{ ok: boolean }>(`/api/sites/${siteId}/gsc/cache`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc'] })
    },
  })
}
